import type { AztecAddress } from "@aztec/aztec.js";
import { atom, computed } from "nanostores";
import {
  connectAztecWallet as connectWallet,
  disconnectAztecWallet as disconnectWallet,
  getAztecWalletAddress,
  isAztecWalletConnected,
  isAztecWalletWindowOpen,
} from "../aztec-utils";

export type AztecWalletState = {
  status: "idle" | "connecting" | "connected" | "error";
  address: AztecAddress | null;
  error: string | null;
};

// Core state atom
const aztecWalletState = atom<AztecWalletState>({
  status: "idle",
  address: null,
  error: null,
});

// Computed values
export const aztecWalletStatus = computed(aztecWalletState, (state) => state.status);
export const aztecWalletAddress = computed(aztecWalletState, (state) => state.address);
export const aztecWalletError = computed(aztecWalletState, (state) => state.error);
export const isAztecConnected = computed(aztecWalletState, (state) => state.status === "connected");

// Actions
export async function connectAztecWallet() {
  console.debug("🔄 Setting wallet state to 'connecting'...");
  aztecWalletState.set({
    status: "connecting",
    address: null,
    error: null,
  });

  try {
    console.debug("🔗 Calling connectWallet() from aztec-utils...");
    await connectWallet();
    console.debug("✅ connectWallet() completed, getting address...");

    const address = await getAztecWalletAddress();
    console.debug("🏠 Retrieved wallet address:", address?.toString());

    console.debug("🔄 Setting wallet state to 'connected'...");
    aztecWalletState.set({
      status: "connected",
      address,
      error: null,
    });

    console.debug("🎯 Starting wallet window monitoring...");
    // Start monitoring wallet window
    startWalletWindowMonitoring();

    console.debug("🎉 Wallet connection process completed successfully");
    return address;
  } catch (error) {
    console.error("💥 Wallet connection error:", error);
    const errorMessage = error instanceof Error ? error.message : "Failed to connect Aztec wallet";
    aztecWalletState.set({
      status: "error",
      address: null,
      error: errorMessage,
    });
    throw error;
  }
}

export function disconnectAztecWallet() {
  disconnectWallet();
  stopWalletWindowMonitoring();

  aztecWalletState.set({
    status: "idle",
    address: null,
    error: null,
  });
}

// Window monitoring
let windowMonitorInterval: NodeJS.Timeout | null = null;

function startWalletWindowMonitoring() {
  console.debug("👁️ Starting wallet window monitoring...");
  stopWalletWindowMonitoring(); // Clear any existing interval

  windowMonitorInterval = setInterval(() => {
    const isWindowOpen = isAztecWalletWindowOpen();
    const currentStatus = aztecWalletState.get().status;

    // Only log when there's a state change or issue
    const shouldDisconnect = !isWindowOpen && currentStatus === "connected";
    if (shouldDisconnect) {
      console.debug("🚪 Wallet window was closed, disconnecting...");
      // Wallet window was closed
      disconnectAztecWallet();
    }
  }, 1000);
}

function stopWalletWindowMonitoring() {
  if (windowMonitorInterval) {
    clearInterval(windowMonitorInterval);
    windowMonitorInterval = null;
  }
}

// Initialize state on load
export async function initializeAztecWalletState() {
  console.debug("🔄 Initializing Aztec wallet state...");

  if (isAztecWalletConnected()) {
    console.debug("💡 Found existing wallet connection, restoring state...");
    const address = await getAztecWalletAddress();
    console.debug("🏠 Retrieved existing wallet address:", address?.toString());

    aztecWalletState.set({
      status: "connected",
      address,
      error: null,
    });

    console.debug("👁️ Starting monitoring for existing connection...");
    startWalletWindowMonitoring();
  } else {
    console.debug("❌ No existing wallet connection found");
  }
}

// Clean up on unload
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    stopWalletWindowMonitoring();
  });
}
